import { Injectable } from '@angular/core';
import { DataState, ListResponse, LoadingState, MLMeasurement, MlModelWithSegmentationSettings, Selected, WorkflowSimple } from '@ca/core/models';
import { MachineLearningService, WorkflowV1Service } from '@ca/core/services';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class CreateApplicationDialogFacadeService {
    private measurementsState$$ = new BehaviorSubject<DataState<Selected<MLMeasurement>[]>>({ loadingState: LoadingState.INIT });
    private allMachineLearningModelsState$$ = new BehaviorSubject<DataState<MlModelWithSegmentationSettings[]>>({ loadingState: LoadingState.INIT });
    private workflowsState$$ = new BehaviorSubject<DataState<WorkflowSimple[]>>({ loadingState: LoadingState.INIT });

    get measurementsState$(): Observable<DataState<Selected<MLMeasurement>[]>> {
        return this.measurementsState$$.asObservable();
    }

    get allMachineLearningModelsState$(): Observable<DataState<MlModelWithSegmentationSettings[]>> {
        return this.allMachineLearningModelsState$$.asObservable();
    }

    get workflowsState$(): Observable<DataState<WorkflowSimple[]>> {
        return this.workflowsState$$.asObservable();
    }

    constructor(
        private machineLearningService: MachineLearningService,
        private workflowV1Service: WorkflowV1Service,
    ) {}

    loadMeasurements(): void {
        this.measurementsState$$.next({ loadingState: LoadingState.LOADING });
        this.machineLearningService
            .getMeasurements$()
            .pipe(
                map((measurements: MLMeasurement[]) => {
                    const data = measurements.map((m: MLMeasurement) => {
                        return { child: m, selected: true } as Selected<MLMeasurement>;
                    });
                    this.measurementsState$$.next({
                        loadingState: LoadingState.LOADED,
                        data,
                    });
                }),
                catchError(() => {
                    this.measurementsState$$.next({
                        loadingState: LoadingState.LOADED,
                        errorState: { errorMsg: 'APPLICATIONS.CREATE_APPLICATION_DIALOG.ERRORS.GET_MEASUREMENTS' },
                    });
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    loadModels(): void {
        this.allMachineLearningModelsState$$.next({ loadingState: LoadingState.LOADING });
        this.machineLearningService
            .fetchAllMachineLearningModels(true)
            .pipe(
                map((models: MlModelWithSegmentationSettings[]) => {
                    this.allMachineLearningModelsState$$.next({ loadingState: LoadingState.LOADED, data: models } as DataState<
                        MlModelWithSegmentationSettings[]
                    >);
                }),
                catchError(() => {
                    this.allMachineLearningModelsState$$.next({
                        loadingState: LoadingState.LOADED,
                        errorState: { errorMsg: 'APPLICATIONS.CREATE_APPLICATION_DIALOG.ERRORS.GET_TRAINING_MODELS' },
                    });
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    loadWorkflows(): void {
        const params = new HttpParams()
            .set('page[number]', '1')
            .set('page[size]', '1000')
            .set('sort', '-createdAt')
            .set('includeOwn', 'true')
            .set('includePublic', 'false');
        this.workflowsState$$.next({ loadingState: LoadingState.LOADING });
        this.workflowV1Service
            .getPrivateOverviewList(params)
            .pipe(
                map((response: ListResponse<WorkflowSimple>) => {
                    const workflows: WorkflowSimple[] = response.data;
                    this.workflowsState$$.next({ loadingState: LoadingState.LOADED, data: workflows } as DataState<WorkflowSimple[]>);
                }),
                catchError(() => {
                    this.workflowsState$$.next({
                        loadingState: LoadingState.LOADED,
                        errorState: { errorMsg: 'APPLICATIONS.CREATE_APPLICATION_DIALOG.ERRORS.GET_WORKFLOWS' },
                    });
                    return EMPTY;
                }),
            )
            .subscribe();
    }
}
